// src/pages/Regularpaper.jsx
import React, { useEffect, useState, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Regularpaper.css';
import { FaBell, FaUserCircle, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';

const API_BASE = 'https://digital.tifleducation.com/admin/public/api';
const LOCAL_SELECTED_KEY = 'selectedQuestionsByChapter';
const LOCAL_STATE_KEY = 'regularPaperState';

// Normalize type labels to consistent keys
const normalizeTypeKey = (label) => {
  if (!label) return '';
  const s = String(label).toLowerCase().replace(/[\.\-\/:]/g, ' ').replace(/\s+/g, ' ').trim();

  if (s.includes('mcq') || s.includes('m c q')) return 'mcq';
  if (s.includes('fill') && s.includes('blank')) return 'fill_in_the_blanks';
  if (s.includes('true') && s.includes('false')) return 'true_false';
  if (s.includes('very') && s.includes('short')) return 'very_short_qa';
  if ((s.includes('short') && s.includes('answer')) && !s.includes('very')) return 'short_qa';
  if (s.includes('long') && s.includes('answer')) return 'long_qa';
  if (s.includes('matching')) return 'matching_type';
  if (s.includes('diagram') || s.includes('image')) return 'diagram_image_based';

  return s.replace(/\s+/g, '_');
};

const Regularpaper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openChapter, setOpenChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [allowedCounts, setAllowedCounts] = useState({});
  const [isViewMode, setIsViewMode] = useState(false);
  const [paperData, setPaperData] = useState(null);
  const [boardId, setBoardId] = useState(null);
  const [gradeId, setGradeId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const toggleDropdown = () => setShowDropdown((prev) => !prev);
  const dropdownRef = useRef(null);
  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });
  const [user, setUser] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
  });

  // Fetch dashboard & user data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getDashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || '',
              email: res.data.user.email || '',
              password: '', // empty for editing
            });
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };

    fetchDashboardData();
  }, []);
  // Handle save changes
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://digital.tifleducation.com/admin/public/api/update-profile",
        {
          name: user.name,
          password: user.password,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Profile updated successfully!");

      setIsModalOpen(false);
      setUser({ ...user, password: '' }); // reset password field
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);

      toast.error("Failed to update profile");

    }
  };
  // --- Load initial state ---
  useEffect(() => {
    const savedState = JSON.parse(localStorage.getItem(LOCAL_STATE_KEY) || '{}');

    const bId = state.boardId || savedState.boardId;
    const gId = state.gradeId || savedState.gradeId;
    const sId = state.subjectId || savedState.subjectId;
    const counts = state.counts || savedState.counts;

    if (state.paperId) {
      setIsViewMode(true);
      fetchPaperById(state.paperId);
    } else if (bId && gId && sId) {
      setBoardId(bId);
      setGradeId(gId);
      setSubjectId(sId);

      const c = counts || {};
      setAllowedCounts({
        mcq: Number(c.mcq || 0),
        fill_in_the_blanks: Number(c.fill_in_the_blanks || 0),
        true_false: Number(c.true_false || 0),
        very_short_qa: Number(c.very_short_qa || 0),
        short_qa: Number(c.short_qa || 0),
        long_qa: Number(c.long_qa || 0),
        matching_type: Number(c.matching_type || 0),
        diagram_image_based: Number(c.diagram_image_based || 0),
      });


      restoreSelectedQuestionsFromLocal();
      fetchQuestionsForSelection(bId, gId, sId);
    } else {
      toast.error('Missing necessary data. Go back to Papers and re-start.');
      navigate('/papers');
    }
    // eslint-disable-next-line
  }, []);

  // --- Persist allowed counts & IDs ---
  useEffect(() => {
    if (boardId && gradeId && subjectId) {
      localStorage.setItem(
        LOCAL_STATE_KEY,
        JSON.stringify({
          boardId,
          gradeId,
          subjectId,
          counts: allowedCounts,
        })
      );
    }
  }, [boardId, gradeId, subjectId, allowedCounts]);

  // --- Restore selections ---
  const restoreSelectedQuestionsFromLocal = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
      const normalized = {};
      Object.entries(saved).forEach(([chapterId, types]) => {
        normalized[chapterId] = {};
        Object.entries(types || {}).forEach(([typeKey, arr]) => {
          normalized[chapterId][typeKey] = new Set(Array.isArray(arr) ? arr.map(Number) : []);
        });
      });
      setSelectedQuestions(normalized);
    } catch (e) {
      console.warn('Failed to restore selections from localStorage', e);
    }
  };

  // --- Fetch questions ---
  const fetchQuestionsForSelection = async (board, grade, subject) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE}/papers/fetch-questions`,
        { board, grade, subject },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = res.data || {};
      const transformed = Object.entries(data).map(([chapterId, info]) => {
        const typesArray = Object.entries(info.types || {}).map(([typeLabel, questions]) => {
          const key = normalizeTypeKey(typeLabel);
          return {
            label: typeLabel,
            key,
            questions: Array.isArray(questions) ? questions : questions.data || [],
          };
        });
        return { id: Number(chapterId), name: info.chapter_name, types: typesArray };
      });

      setChapters(transformed);
    } catch (err) {
      console.error('fetchQuestions error', err);
      toast.error('Failed to load questions for this subject.');
    }
  };

  // --- Fetch paper in view mode ---
  const fetchPaperById = async (paperId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/papers/${paperId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaperData(res.data || {});
    } catch (err) {
      console.error('fetchPaperById', err);
      toast.error('Failed to load paper.');
    }
  };

  const toggleChapter = (index) => setOpenChapter(openChapter === index ? null : index);

  // --- Counts validation ---
  const getSelectedCountForType = (typeKey) => {
    let count = 0;
    Object.values(selectedQuestions).forEach((types) => {
      count += (types[typeKey]?.size || 0);
    });
    return count;
  };

  const validateCountsBeforeSave = () => {
    for (const [key, allowed] of Object.entries(allowedCounts)) {
      const selected = getSelectedCountForType(key);
      if (selected < allowed) {
        toast.error(`You must select ${allowed} question(s) for ${key}, selected only ${selected}.`);
        return false;
      }
      if (selected > allowed) {
        toast.error(`You can only select ${allowed} question(s) for ${key}, selected ${selected}.`);
        return false;
      }
    }
    return true;
  };

  const buildSelectedQuestionsPayload = () => {
    const payload = {};
    Object.entries(selectedQuestions).forEach(([chapterId, types]) => {
      payload[chapterId] = {};
      Object.entries(types).forEach(([typeKey, set]) => {
        payload[chapterId][typeKey] = Array.from(set);
      });
    });
    return payload;
  };

  const handleSavePaper = () => {
    // Restore latest selections
    const savedSelections = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
    const normalized = {};
    Object.entries(savedSelections).forEach(([chapterId, types]) => {
      normalized[chapterId] = {};
      Object.entries(types || {}).forEach(([typeKey, arr]) => {
        normalized[chapterId][typeKey] = new Set(Array.isArray(arr) ? arr.map(Number) : []);
      });
    });
    setSelectedQuestions(normalized);
    console.log("Saved selections from localStorage:", savedSelections);
    // Validate counts
    const getSelectedCountForType = (typeKey) => {
      let count = 0;
      Object.values(normalized).forEach((types) => {
        count += (types[typeKey]?.size || 0);
      });
      return count;
    };

    for (const [key, allowed] of Object.entries(allowedCounts)) {
      const selected = getSelectedCountForType(key);
      if (selected < allowed) {
        toast.error(`You must select ${allowed} question(s) for ${key}, selected only ${selected}.`);
        return;
      }
      if (selected > allowed) {
        toast.error(`You can only select ${allowed} question(s) for ${key}, selected ${selected}.`);
        return;
      }
    }

    // Prepare payload
    const payload = {};
    Object.entries(normalized).forEach(([chapterId, types]) => {
      payload[chapterId] = {};
      Object.entries(types).forEach(([typeKey, set]) => {
        payload[chapterId][typeKey] = Array.from(set);
      });
    });

    localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(payload));

    toast.success('Selected questions saved successfully!');
    navigate('/preview', {
      state: {
        boardId,
        gradeId,
        subjectId,
        counts: allowedCounts,
        selected_questions: payload,
      },
    });
  };

  // --- VIEW MODE ---
  if (isViewMode && paperData) {
    const { paper, sections } = paperData;
    return (
      <div className="dashboard-container">
        <Sidebar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
        />
        <main className="main-content">
          <div className="regular-header">
            <div className="regular-welcome-text">
              <button className="back-link" onClick={() => navigate('/papers')}>Back</button>
              <span className="regular-welcome-text">Preview Paper</span>
            </div>
            <div className="header-icons" ref={dropdownRef}>
              <FaBell className="icon" />
              <FaUserCircle className="icon" />
            </div>
          </div>

          <div className="paper-info">
            <h2>{paper?.paper_name}</h2>
            <p>Date: {paper?.date}</p>
            <p>Total Marks: {paper?.total_marks}</p>
            <p>Duration: {paper?.duration}</p>
          </div>

          <div className="sections">
            {sections && Object.entries(sections).map(([sectionKey, groups]) => (
              <div key={sectionKey} className="section-block">
                <h3>{sectionKey.replace(/_/g, ' ')}</h3>
                {Array.isArray(groups) && groups.map((group, idx) => (
                  <div key={idx} className="chapter-group">
                    <h4>Chapter ID: {group.chapter_id}</h4>
                    <ol>
                      {Array.isArray(group.questions) && group.questions.map(q => (
                        <li key={q.id}>{q.question || q.question_text || q.title || `Question #${q.id}`}</li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // --- SELECTION MODE ---
  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />
      <main className="main-content">
        <div className="regular-header">
          <div className="regular-welcome-text">
            <a
              onClick={() =>
                navigate('/selectregularpaper', {
                  state: {
                    boardId,
                    gradeId,
                    subjectId,
                    counts: {
                      mcq: allowedCounts.mcq,
                      fill: allowedCounts.fill_in_the_blanks,
                      truefalse: allowedCounts.true_false,
                      veryshort: allowedCounts.very_short_qa,
                      short: allowedCounts.short_qa,
                      long: allowedCounts.long_qa,
                      matching: allowedCounts.matching_type,
                      diagram: allowedCounts.diagram_image_based,
                    },
                  },
                })
              }
              className="back-link"
            >
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="regular-welcome-text">Add Questions - Select ChapterWise</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className='edituser' onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="chapter-list">
          {chapters.map((chapter, index) => (
            <div key={chapter.id} className="chapter-box">
              <div className="chapter-header" onClick={() => toggleChapter(index)}>
                <div><strong>{chapter.name}</strong></div>
                {openChapter === index ? <FaChevronDown /> : <FaChevronRight />}
              </div>

              {openChapter === index && (
                <div className="chapter-options">
                  {chapter.types.map((type) => {
                    const routeMap = {
                      mcq: '/mcq',
                      fill_in_the_blanks: '/fill-blanks',
                      true_false: '/true-false',
                      very_short_qa: '/veryshort-answers',
                      short_qa: '/short-answers',
                      long_qa: '/long-answers',
                      matching_type: '/matching',
                      diagram_image_based: '/diagram',
                    };
                    const route = routeMap[type.key] || '/mcq';

                    return (
                      <div
                        key={type.key}
                        className="chapter-option"
                        onClick={() =>
                          navigate(route, {
                            state: {
                              boardId,
                              gradeId,
                              subjectId,
                              chapterId: chapter.id,
                              typeKey: type.key,
                            },
                          })
                        }
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        <span>{type.label}</span>
                        <span>({type.questions?.length || 0})</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'right', margin: '16px 20px 20px 20px' }}>
          <button onClick={handleSavePaper} className="preview-btn">Save Paper</button>
        </div>
      </main>
      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Modal Styles -----
const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};
export default Regularpaper;
